import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Timer, 
  Lightning,
  Envelope,
  Chat,
  Code,
  Phone,
  Plus,
  Trash,
  Check,
  CircleNotch,
  WarningCircle,
  ArrowRight,
  MagicWand
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import MentionInput from './ui/MentionInput';
import JsonMentionTextarea from './ui/JsonMentionTextarea';
import CreateCustomFieldModal from './CreateCustomFieldModal';

const PostCallActionModal = ({ 
  isOpen, 
  onClose, 
  actionToEdit = null,
  categories = [],
  isInline = false
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    enabled: true,
    triggerType: 'always',
    actionType: 'email',
    priority: 100,
    conditions: []
  });

  // Variable extractions state
  const [extractions, setExtractions] = useState([]);

  // State for qualifiers and custom fields
  const [qualifiers, setQualifiers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [isCustomFieldModalOpen, setIsCustomFieldModalOpen] = useState(false);

  // Action-specific configurations
  const [emailConfig, setEmailConfig] = useState({
    to_email_template: '',
    from_name: '',
    subject_template: '',
    body_template: '',
    cc_email_template: '',
    bcc_email_template: '',
    reply_to_email: ''
  });

  const [smsConfig, setSmsConfig] = useState({
    to_number_template: '',
    message_template: '',
    media_urls: []
  });

  const [apiConfig, setApiConfig] = useState({
    url: '',
    method: 'POST',
    headers: {},
    body_template: {},
    auth_type: 'none',
    auth_config: {},
    timeout_seconds: 30,
    retry_attempts: 3
  });

  const [phoneConfig, setPhoneConfig] = useState({
    to_number_template: '',
    delay_minutes: 0,
    max_attempts: 3,
    script_template: '',
    transfer_number: '',
    voicemail_template: ''
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  // Available built-in fields (only customer_phone works)
  const builtInFields = [
    { value: 'customer_phone', label: 'Customer Phone' }
  ];

  // Helper functions for extractions
  const addExtraction = () => {
    setExtractions(prev => [
      ...prev,
      {
        variable_name: '',
        extraction_prompt: ''
      }
    ]);
  };

  const removeExtraction = (index) => {
    setExtractions(prev => prev.filter((_, i) => i !== index));
  };

  const updateExtraction = (index, field, value) => {
    setExtractions(prev => prev.map((extraction, i) => 
      i === index ? { ...extraction, [field]: value } : extraction
    ));
  };

  // Get all available variables (built-in + custom)
  const getAllVariables = () => {
    const customVariables = extractions
      .filter(ext => ext.variable_name.trim())
      .map(ext => ({ value: ext.variable_name, label: ext.variable_name }));

    return [...builtInFields, ...customVariables];
  };

  // Get all available field options for conditions
  const getAllFieldOptions = () => {
    const qualifierFields = qualifiers.map(qualifier => ({
      value: qualifier.topic_name,
      label: qualifier.topic_name,
      type: 'qualifier',
      possible_answers: qualifier.possible_answers
    }));

    const customFieldOptions = customFields.map(field => ({
      value: field.field_name,
      label: field.field_name,
      type: 'custom'
    }));

    return [
      ...builtInFields.map(field => ({ ...field, type: 'builtin' })),
      ...qualifierFields,
      ...customFieldOptions
    ];
  };

  // Get variables in format expected by mention components
  const getMentionVariables = () => {
    const allVariables = getAllVariables();
    return allVariables.map(variable => ({
      id: variable.value,
      text: variable.value
    }));
  };

  // Load existing action data
  useEffect(() => {
    if (actionToEdit) {
      loadActionData();
    }
  }, [actionToEdit]);

  // Load qualifiers and custom fields on mount
  useEffect(() => {
    loadQualifiers();
    loadCustomFields();
  }, []);

  const loadActionData = async () => {
    if (!actionToEdit) return;

    try {
      setLoading(true);

      // Set basic form data
      setFormData({
        name: actionToEdit.name || '',
        description: actionToEdit.description || '',
        enabled: actionToEdit.enabled ?? true,
        triggerType: actionToEdit.triggerType || 'always',
        actionType: actionToEdit.actionType || 'email',
        priority: actionToEdit.priority || 100,
        conditions: actionToEdit.conditions || []
      });

      // Load extractions if they exist
      if (actionToEdit.id) {
        const { data: extractionData, error: extractionError } = await supabase
          .from('brain_post_call_extractions')
          .select('*')
          .eq('setting_id', actionToEdit.id)
          .order('created_at');

        if (!extractionError && extractionData) {
          setExtractions(extractionData.map(ext => ({
            variable_name: ext.variable_name,
            extraction_prompt: ext.extraction_config?.prompt || ''
          })));
        }
      }

      // Load action-specific configuration
      if (actionToEdit.actionType && actionToEdit.action_id) {
        const tableName = `brain_post_call_${actionToEdit.actionType}_actions`;
        const { data, error } = await supabase
          .from(tableName)
          .select('*')
          .eq('id', actionToEdit.action_id)
          .single();

        if (error) throw error;

        // Set the appropriate config based on action type
        switch (actionToEdit.actionType) {
          case 'email':
            setEmailConfig({
              to_email_template: data.to_email_template,
              from_name: data.from_name,
              subject_template: data.subject_template,
              body_template: data.body_template,
              cc_email_template: data.cc_email_template,
              bcc_email_template: data.bcc_email_template,
              reply_to_email: data.reply_to_email
            });
            break;
          case 'sms':
            setSmsConfig({
              to_number_template: data.to_number_template,
              message_template: data.message_template,
              media_urls: data.media_urls
            });
            break;
          case 'api':
            setApiConfig(data);
            break;
          case 'phone':
            setPhoneConfig({
              to_number_template: data.to_number_template,
              delay_minutes: data.delay_minutes,
              max_attempts: data.max_attempts,
              script_template: data.script_template,
              transfer_number: data.transfer_number,
              voicemail_template: data.voicemail_template
            });
            break;
        }
      }
    } catch (err) {
      console.error('Error loading action data:', err);
      toast.error('Failed to load action data');
    } finally {
      setLoading(false);
    }
  };

  // Load qualifiers from brain_conversation_topics
  const loadQualifiers = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) return;

      const { data, error } = await supabase
        .from('brain_conversation_topics')
        .select('*')
        .eq('account_id', accountId)
        .order('topic_name');

      if (error) throw error;
      setQualifiers(data || []);
    } catch (err) {
      console.error('Error loading qualifiers:', err);
      setQualifiers([]);
    }
  };

  // Load custom fields from brain_post_call_custom_fields
  const loadCustomFields = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) return;

      const { data, error } = await supabase
        .from('brain_post_call_custom_fields')
        .select('*')
        .eq('account_id', accountId)
        .order('field_name');

      if (error) throw error;
      setCustomFields(data || []);
    } catch (err) {
      console.error('Error loading custom fields:', err);
      setCustomFields([]);
    }
  };

  // Handle creating a new custom field
  const handleCreateCustomField = (newField) => {
    // Add to local state
    setCustomFields(prev => [...prev, newField]);

    // Auto-select the new field in the condition
    if (formData.conditions.length > 0) {
      const lastConditionIndex = formData.conditions.length - 1;
      updateCondition(lastConditionIndex, 'field_name', newField.field_name);
    }
  };

  const handleDelete = async () => {
    if (!actionToEdit?.id) return;

    try {
      setLoading(true);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated');

      // Delete action-specific configuration first
      if (actionToEdit.action_id && actionToEdit.actionType) {
        const tableName = `brain_post_call_${actionToEdit.actionType}_actions`;
        await supabase
          .from(tableName)
          .delete()
          .eq('id', actionToEdit.action_id);
      }

      // Delete associated extractions
      await supabase
        .from('brain_post_call_extractions')
        .delete()
        .eq('setting_id', actionToEdit.id);

      // Delete associated conditions
      await supabase
        .from('brain_post_call_conditions')
        .delete()
        .eq('setting_id', actionToEdit.id);

      // Delete main settings record
      await supabase
        .from('brain_post_call_settings')
        .delete()
        .eq('id', actionToEdit.id);

      toast.success('Post Call Action deleted');
      onClose(true);
    } catch (err) {
      console.error('Error deleting post call action:', err);
      toast.error('Failed to delete post call action');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Validation
      if (!formData.name.trim()) {
        toast.error('Please enter a name for the action');
        return;
      }

      // Validate extractions
      for (let i = 0; i < extractions.length; i++) {
        const extraction = extractions[i];
        if (!extraction.variable_name.trim()) {
          toast.error(`Please enter a variable name for extraction ${i + 1}`);
          return;
        }
        if (!extraction.extraction_prompt.trim()) {
          toast.error(`Please enter an extraction prompt for ${extraction.variable_name}`);
          return;
        }
        // Ensure variable name is valid (alphanumeric + underscore only)
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(extraction.variable_name)) {
          toast.error(`Variable name "${extraction.variable_name}" must start with a letter and contain only letters, numbers, and underscores`);
          return;
        }
      }

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated');

      // Get AI phone number for SMS and phone actions
      let aiPhoneNumber = null;
      if (formData.actionType === 'sms' || formData.actionType === 'phone') {
        const { data: phoneData, error: phoneError } = await supabase
          .from('vapi_call_settings')
          .select('phone_number')
          .eq('account_id', accountId)
          .single();

        if (phoneError) {
          console.error('Error fetching AI phone number:', phoneError);
          toast.error('Error: Could not fetch AI phone number. Please check your VAPI settings.');
          return;
        }

        aiPhoneNumber = phoneData?.phone_number;
        if (!aiPhoneNumber) {
          toast.error('Error: No AI phone number found in VAPI settings. Please configure your phone number first.');
          return;
        }
      }

      // First, save the action-specific configuration
      let actionId;
      const tableName = `brain_post_call_${formData.actionType}_actions`;

      // Get the appropriate config based on action type
      let configData;
      switch (formData.actionType) {
        case 'email':
          configData = { ...emailConfig, account_id: accountId, from_email: 'bella@thesavants.ai' };
          break;
        case 'sms':
          configData = { ...smsConfig, account_id: accountId, from_number: aiPhoneNumber };
          break;
        case 'api':
          configData = { ...apiConfig, account_id: accountId };
          break;
        case 'phone':
          configData = { ...phoneConfig, account_id: accountId, from_number: aiPhoneNumber };
          break;
      }

      if (actionToEdit?.action_id) {
        // Update existing action configuration
        const { error } = await supabase
          .from(tableName)
          .update(configData)
          .eq('id', actionToEdit.action_id);

        if (error) throw error;
        actionId = actionToEdit.action_id;
      } else {
        // Create new action configuration
        const { data, error } = await supabase
          .from(tableName)
          .insert(configData)
          .select()
          .single();

        if (error) throw error;
        actionId = data.id;
      }

      // Now save the main settings
      const settingsData = {
        account_id: accountId,
        name: formData.name,
        description: formData.description,
        enabled: formData.enabled,
        trigger_type: formData.triggerType,
        action_type: formData.actionType,
        action_id: actionId,
        priority: formData.priority
      };

      let savedSettings;
      if (actionToEdit?.id) {
        // Update existing settings
        const { data, error } = await supabase
          .from('brain_post_call_settings')
          .update(settingsData)
          .eq('id', actionToEdit.id)
          .select()
          .single();

        if (error) throw error;
        savedSettings = data;
      } else {
        // Create new settings
        const { data, error } = await supabase
          .from('brain_post_call_settings')
          .insert(settingsData)
          .select()
          .single();

        if (error) throw error;
        savedSettings = data;
      }

      // Save extractions
      if (savedSettings.id) {
        // Delete existing extractions
        await supabase
          .from('brain_post_call_extractions')
          .delete()
          .eq('setting_id', savedSettings.id);

        // Insert new extractions
        if (extractions.length > 0) {
          const extractionsToInsert = extractions
            .filter(ext => ext.variable_name.trim() && ext.extraction_prompt.trim())
            .map(extraction => ({
              setting_id: savedSettings.id,
              variable_name: extraction.variable_name.trim(),
              extraction_type: 'ai',
              extraction_config: { prompt: extraction.extraction_prompt.trim() }
            }));

          if (extractionsToInsert.length > 0) {
            const { error: extractionError } = await supabase
              .from('brain_post_call_extractions')
              .insert(extractionsToInsert);

            if (extractionError) throw extractionError;
          }
        }
      }

      // Save conditions if any
      if (formData.triggerType === 'conditional' && formData.conditions.length > 0) {
        // Delete existing conditions
        await supabase
          .from('brain_post_call_conditions')
          .delete()
          .eq('setting_id', savedSettings.id);

        // Insert new conditions
        const conditionsToInsert = formData.conditions.map(condition => ({
          setting_id: savedSettings.id,
          field_path: condition.field_name,
          operator: condition.operator,
          value: condition.value,
          case_sensitive: condition.case_sensitive || false
        }));

        const { error } = await supabase
          .from('brain_post_call_conditions')
          .insert(conditionsToInsert);

        if (error) throw error;
      }

      toast.success(actionToEdit ? 'Post Call Action updated' : 'Post Call Action created');
      onClose(true, savedSettings);
    } catch (err) {
      console.error('Error saving post call action:', err);
      toast.error('Failed to save post call action');
    } finally {
      setLoading(false);
    }
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        {
          field_name: 'customer_name',
          operator: 'contains',
          value: '',
          case_sensitive: false
        }
      ]
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) => 
        i === index ? { ...condition, [field]: value } : condition
      )
    }));
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'email': return Envelope;
      case 'sms': return Chat;
      case 'api': return Code;
      case 'phone': return Phone;
      default: return Timer;
    }
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  const renderContent = () => (
    <motion.div 
      className={cn(
        "bg-white flex flex-col",
        isInline 
          ? "w-full h-full overflow-auto" 
          : "rounded-2xl shadow-xl w-[90vw] h-[90vh] relative border border-gray-200/50 overflow-hidden"
      )}
      initial={!isInline ? { scale: 0.95, opacity: 0 } : {}}
      animate={!isInline ? { scale: 1, opacity: 1 } : {}}
      exit={!isInline ? { scale: 0.95, opacity: 0 } : {}}
      onClick={e => e.stopPropagation()}
    >
      {isInline ? (
        // Document-style inline interface
        <div className="h-full bg-white flex flex-col">
          {/* Header */}
          <motion.div 
            className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white/95 backdrop-blur-md z-20 flex-shrink-0"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100/80 flex items-center justify-center">
                <Timer className="h-4 w-4 text-gray-700" />
              </div>
              <div>
                <h2 className="text-base font-medium text-gray-900">
                  {actionToEdit ? 'Edit Post Call Action' : 'Create Post Call Action'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Automate actions that trigger after phone calls end
                </p>
              </div>
            </div>

            <motion.button
              type="button"
              disabled={loading}
              onClick={handleSave}
              className={cn(
                "px-5 py-2.5 bg-black hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-1",
                "disabled:opacity-60 disabled:cursor-not-allowed"
              )}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <CircleNotch className="animate-spin h-4 w-4 text-white" />
                  <span>Saving</span>
                </span>
              ) : (
                <span>{actionToEdit ? 'Save Changes' : 'Create Action'}</span>
              )}
            </motion.button>
          </motion.div>

          {/* Document-style content */}
          <div className="flex-1 bg-gradient-to-br from-gray-50/30 via-white to-purple-50/10 overflow-y-auto">
            <motion.div 
              className="min-h-full flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <div className="w-full max-w-4xl mx-auto">
                <div className="bg-white shadow-[0_2px_20px_rgba(0,0,0,0.08)] border-0 backdrop-blur-sm">
                  <div className="px-8 sm:px-16 py-8 sm:py-12 space-y-8 sm:space-y-12">

                    {/* Basic Information */}
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-1 h-6 bg-gradient-to-b from-gray-700 to-gray-800 rounded-full"></div>
                        <h3 className="text-lg font-medium text-gray-900 tracking-wide">Basic Information</h3>
                      </div>

                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm placeholder:text-gray-400 transition-all duration-200"
                            placeholder="e.g., Send Thank You Email"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm placeholder:text-gray-400 resize-none transition-all duration-200"
                            rows={3}
                            placeholder="Describe what this action does..."
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700">
                            Enabled
                          </label>
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, enabled: !prev.enabled }))}
                            className={cn(
                              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                              formData.enabled ? "bg-black" : "bg-gray-200"
                            )}
                          >
                            <span
                              className={cn(
                                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                formData.enabled ? "translate-x-6" : "translate-x-1"
                              )}
                            />
                          </button>
                        </div>
                      </div>
                    </motion.div>

                    {/* Action Type Selection */}
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-1 h-6 bg-gradient-to-b from-gray-700 to-gray-800 rounded-full"></div>
                        <h3 className="text-lg font-light text-gray-800 tracking-wide">Action Type</h3>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: 'email', label: 'Send Email', icon: Envelope, color: 'blue' },
                          { value: 'sms', label: 'Send SMS', icon: Chat, color: 'green' },
                          { value: 'api', label: 'API Call', icon: Code, color: 'purple' },
                          { value: 'phone', label: 'Make Call', icon: Phone, color: 'amber' }
                        ].map(action => {
                          const IconComponent = action.icon;
                          const isSelected = formData.actionType === action.value;

                          return (
                            <button
                              key={action.value}
                              onClick={() => setFormData(prev => ({ ...prev, actionType: action.value }))}
                              className={cn(
                                "p-6 rounded-xl border-2 transition-all",
                                isSelected
                                  ? "border-black bg-gray-50"
                                  : "border-gray-200 hover:border-gray-300"
                              )}
                            >
                              <IconComponent className={cn(
                                "w-8 h-8 mb-3 mx-auto",
                                isSelected ? "text-black" : "text-gray-400"
                              )} />
                              <div className={cn(
                                "text-sm font-medium",
                                isSelected ? "text-gray-900" : "text-gray-600"
                              )}>
                                {action.label}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>

                    {/* Trigger Conditions Section */}
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.42, duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-1 h-6 bg-gradient-to-b from-gray-700 to-gray-800 rounded-full"></div>
                        <h3 className="text-lg font-medium text-gray-900 tracking-wide">When to Trigger</h3>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Trigger Type
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, triggerType: 'always' }))}
                              className={cn(
                                "p-4 rounded-xl border-2 transition-all text-sm",
                                formData.triggerType === 'always'
                                  ? "border-black bg-gray-50 text-gray-900"
                                  : "border-gray-200 hover:border-gray-300 text-gray-600"
                              )}
                            >
                              <div className="font-medium mb-1">Always</div>
                              <div className="text-xs opacity-75">Run after every call</div>
                            </button>

                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, triggerType: 'conditional' }))}
                              className={cn(
                                "p-4 rounded-xl border-2 transition-all text-sm",
                                formData.triggerType === 'conditional'
                                  ? "border-black bg-gray-50 text-gray-900"
                                  : "border-gray-200 hover:border-gray-300 text-gray-600"
                              )}
                            >
                              <div className="font-medium mb-1">Conditional</div>
                              <div className="text-xs opacity-75">Run only when conditions match</div>
                            </button>
                          </div>
                        </div>

                        {formData.triggerType === 'conditional' && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-4"
                          >
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                              <div className="flex items-start">
                                <WarningCircle className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                                <div>
                                  <h4 className="text-sm font-medium text-amber-900 mb-1">Conditional Triggers</h4>
                                  <p className="text-sm text-amber-700">
                                    Add conditions to control when this action runs. For example, only send emails when the call duration is over 2 minutes.
                                  </p>
                                </div>
                              </div>
                            </div>

                            {formData.conditions.length > 0 && (
                              <div className="space-y-3">
                                {formData.conditions.map((condition, index) => (
                                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-3">
                                      <h4 className="text-sm font-medium text-gray-700">
                                        Condition {index + 1}
                                      </h4>
                                      <button
                                        type="button"
                                        onClick={() => removeCondition(index)}
                                        className="text-red-400 hover:text-red-600 transition-colors p-1"
                                      >
                                        <Trash className="w-4 h-4" />
                                      </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          Field
                                        </label>
                                        <select
                                          value={condition.field_name || ''}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '__create_custom__') {
                                              setIsCustomFieldModalOpen(true);
                                            } else {
                                              updateCondition(index, 'field_name', value);
                                              // Clear value when field changes
                                              updateCondition(index, 'value', '');
                                            }
                                          }}
                                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm"
                                        >
                                          <option value="">Select field...</option>

                                          {/* Built-in fields */}
                                          <optgroup label="Built-in Fields">
                                            <option value="customer_phone">Customer Phone</option>
                                          </optgroup>

                                          {/* Qualifiers */}
                                          {qualifiers.length > 0 && (
                                            <optgroup label="Qualifiers">
                                              {qualifiers.map(qualifier => (
                                                <option key={qualifier.id} value={qualifier.topic_name}>
                                                  {qualifier.topic_name}
                                                </option>
                                              ))}
                                            </optgroup>
                                          )}

                                          {/* Custom fields */}
                                          {customFields.length > 0 && (
                                            <optgroup label="Custom Fields">
                                              {customFields.map(field => (
                                                <option key={field.id} value={field.field_name}>
                                                  {field.field_name}
                                                </option>
                                              ))}
                                            </optgroup>
                                          )}

                                          {/* Create custom option */}
                                          <optgroup label="Actions">
                                            <option value="__create_custom__">+ Create Custom Field</option>
                                          </optgroup>
                                        </select>
                                      </div>

                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          Operator
                                        </label>
                                        <select
                                          value={condition.operator || ''}
                                          onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm"
                                        >
                                          <option value="">Select operator...</option>
                                          <option value="contains">Contains</option>
                                          <option value="not_contains">Does not contain</option>
                                          <option value="equals">Equals</option>
                                          <option value="not_equals">Does not equal</option>
                                          <option value="greater_than">Greater than</option>
                                          <option value="less_than">Less than</option>
                                          <option value="regex_match">Regex match</option>
                                        </select>
                                      </div>

                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          Value
                                        </label>
                                        {(() => {
                                          // Find the selected field to check if it's a qualifier with possible answers
                                          const selectedField = getAllFieldOptions().find(field => field.value === condition.field_name);
                                          const isQualifierWithAnswers = selectedField?.type === 'qualifier' && 
                                                                        selectedField.possible_answers && 
                                                                        selectedField.possible_answers.length > 0;

                                          if (isQualifierWithAnswers) {
                                            return (
                                              <select
                                                value={condition.value || ''}
                                                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm"
                                              >
                                                <option value="">Select answer...</option>
                                                {selectedField.possible_answers.map((answer, answerIndex) => (
                                                  <option key={answerIndex} value={answer}>
                                                    {answer}
                                                  </option>
                                                ))}
                                              </select>
                                            );
                                          } else {
                                            return (
                                              <input
                                                type="text"
                                                value={condition.value || ''}
                                                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm"
                                                placeholder="Enter value..."
                                              />
                                            );
                                          }
                                        })()}
                                      </div>
                                    </div>

                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        id={`case-sensitive-${index}`}
                                        checked={condition.case_sensitive || false}
                                        onChange={(e) => updateCondition(index, 'case_sensitive', e.target.checked)}
                                        className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black/20 focus:ring-2"
                                      />
                                      <label htmlFor={`case-sensitive-${index}`} className="ml-2 text-xs text-gray-600">
                                        Case sensitive comparison
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={addCondition}
                              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-all flex items-center justify-center space-x-2"
                            >
                              <Plus className="w-4 h-4" />
                              <span>Add Condition</span>
                            </button>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>

                    {/* Variable Extraction Section - NEW */}
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.45, duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-1 h-6 bg-gradient-to-b from-gray-700 to-gray-800 rounded-full"></div>
                        <h3 className="text-lg font-medium text-gray-900 tracking-wide">Variable Extraction</h3>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                          <MagicWand className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900 mb-1">Extract Custom Variables</h4>
                            <p className="text-sm text-blue-700">
                              Define custom variables to extract from call transcripts. Use them in your templates with {'{{}}'} syntax.
                            </p>
                          </div>
                        </div>
                      </div>

                      {extractions.length > 0 && (
                        <div className="space-y-4">
                          {extractions.map((extraction, index) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-medium text-gray-700">
                                  Extraction {index + 1}
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => removeExtraction(index)}
                                  className="text-red-400 hover:text-red-600 transition-colors p-1"
                                >
                                  <Trash className="w-4 h-4" />
                                </button>
                              </div>

                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Variable Name
                                  </label>
                                  <input
                                    type="text"
                                    value={extraction.variable_name}
                                    onChange={(e) => updateExtraction(index, 'variable_name', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm transition-all duration-200"
                                    placeholder="e.g., favorite_color"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Use this as {`{{${extraction.variable_name || 'variable_name'}}}`} in your templates
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Extraction Prompt
                                  </label>
                                  <textarea
                                    value={extraction.extraction_prompt}
                                    onChange={(e) => updateExtraction(index, 'extraction_prompt', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm transition-all duration-200"
                                    rows={3}
                                    placeholder="e.g., Extract the customer's favorite color mentioned in the conversation"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Describe what information to extract from the call transcript
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={addExtraction}
                        className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-all flex items-center justify-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Variable Extraction</span>
                      </button>

                      {/* Available Variables Helper */}
                      {(extractions.length > 0 || getAllVariables().length > builtInFields.length) && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Available Variables</h4>
                          <div className="flex flex-wrap gap-2">
                            {getAllVariables().map(variable => (
                              <span
                                key={variable.value}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white border border-gray-200 text-gray-700"
                              >
                                {`{{${variable.value}}}`}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Copy and paste these variable names into your email/SMS templates
                          </p>
                        </div>
                      )}
                    </motion.div>

                    {/* Action Configuration */}
                    <motion.div 
                      className="space-y-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                    >
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-1 h-6 bg-gradient-to-b from-gray-700 to-gray-800 rounded-full"></div>
                        <h3 className="text-lg font-medium text-gray-900 tracking-wide">Configuration</h3>
                      </div>

                      {/* Email Configuration */}
                      {formData.actionType === 'email' && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              To Email
                            </label>
                            <MentionInput
                              value={emailConfig.to_email_template}
                              onChange={(e) => setEmailConfig(prev => ({ ...prev, to_email_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm placeholder:text-gray-400 transition-all duration-200"
                              placeholder="Type @ to insert variables like @customer_email or use static@email.com"
                              name="to_email_template"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Type '@' to see available variables, or use {'{{variable}}'} syntax directly
                            </p>
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700 flex items-center">
                              <Envelope className="w-4 h-4 mr-2" />
                              Email will be sent from bella@thesavants.ai
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                From Name
                              </label>
                              <input
                                type="text"
                                value={emailConfig.from_name}
                                onChange={(e) => setEmailConfig(prev => ({ ...prev, from_name: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm placeholder:text-gray-400 transition-all duration-200"
                                placeholder="Company Name"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Subject
                            </label>
                            <MentionInput
                              value={emailConfig.subject_template}
                              onChange={(e) => setEmailConfig(prev => ({ ...prev, subject_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-transparent text-sm placeholder:text-gray-400 transition-all duration-200"
                              placeholder="Type @ to insert variables like: Thank you for calling @company_name"
                              name="subject_template"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email Body (HTML)
                            </label>
                            <JsonMentionTextarea
                              value={emailConfig.body_template}
                              onChange={(e) => setEmailConfig(prev => ({ ...prev, body_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                              placeholder={`<p>Hi @customer_name,</p>
<p>Thank you for calling us today. I remember that your favorite color is @favorite_color!</p>
<p>Here's a summary of our conversation:</p>
<p>@call_summary</p>
<p>Best regards,<br>@company_name</p>`}
                              name="body_template"
                            />
                          </div>

                          {/* Advanced Email Options */}
                          <div className="space-y-4 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700">Advanced Options</h4>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  CC Email
                                </label>
                                <MentionInput
                                  value={emailConfig.cc_email_template || ''}
                                  onChange={(e) => setEmailConfig(prev => ({ ...prev, cc_email_template: e.target.value }))}
                                  questions={getMentionVariables()}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Optional CC recipients"
                                  name="cc_email_template"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  BCC Email
                                </label>
                                <MentionInput
                                  value={emailConfig.bcc_email_template || ''}
                                  onChange={(e) => setEmailConfig(prev => ({ ...prev, bcc_email_template: e.target.value }))}
                                  questions={getMentionVariables()}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Optional BCC recipients"
                                  name="bcc_email_template"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Reply-To Email
                                </label>
                                <input
                                  type="email"
                                  value={emailConfig.reply_to_email || ''}
                                  onChange={(e) => setEmailConfig(prev => ({ ...prev, reply_to_email: e.target.value }))}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Optional reply-to address"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* SMS Configuration */}
                      {formData.actionType === 'sms' && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              To Number
                            </label>
                            <MentionInput
                              value={smsConfig.to_number_template}
                              onChange={(e) => setSmsConfig(prev => ({ ...prev, to_number_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Type @ to insert variables like @customer_phone or use +1234567890"
                              name="to_number_template"
                            />
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700 flex items-center">
                              <Chat className="w-4 h-4 mr-2" />
                              SMS will be sent from your AI phone number
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Message
                            </label>
                            <MentionInput
                              value={smsConfig.message_template}
                              onChange={(e) => setSmsConfig(prev => ({ ...prev, message_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Type @ to insert variables like: Hi @customer_name! Thanks for calling @company_name. I remember your favorite color is @favorite_color."
                              name="message_template"
                              multiline={true}
                              rows={6}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {smsConfig.message_template.length}/160 characters
                            </p>
                          </div>

                          {/* Advanced SMS Options */}
                          <div className="space-y-4 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700">Advanced Options</h4>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Media URLs (Optional)
                              </label>
                              <textarea
                                value={(smsConfig.media_urls || []).join('\n')}
                                onChange={(e) => {
                                  const urls = e.target.value.split('\n').filter(url => url.trim());
                                  setSmsConfig(prev => ({ ...prev, media_urls: urls }));
                                }}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                                rows={3}
                                placeholder={`https://example.com/image1.jpg
https://example.com/document.pdf
(One URL per line)`}
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Optional media attachments (images, videos, documents). One URL per line.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* API Configuration */}
                      {formData.actionType === 'api' && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              URL
                            </label>
                            <MentionInput
                              value={apiConfig.url}
                              onChange={(e) => setApiConfig(prev => ({ ...prev, url: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Type @ to insert variables like: https://api.example.com/customers/@customer_id/orders"
                              name="api_url"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Type '@' to insert dynamic variables into your API endpoint
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Method
                            </label>
                            <select
                              value={apiConfig.method}
                              onChange={(e) => setApiConfig(prev => ({ ...prev, method: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                              <option value="PUT">PUT</option>
                              <option value="PATCH">PATCH</option>
                              <option value="DELETE">DELETE</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Headers (JSON)
                            </label>
                            <JsonMentionTextarea
                              value={JSON.stringify(apiConfig.headers, null, 2)}
                              onChange={(e) => {
                                try {
                                  const headers = JSON.parse(e.target.value);
                                  setApiConfig(prev => ({ ...prev, headers }));
                                } catch (err) {
                                  // Invalid JSON, just update the text
                                }
                              }}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                              placeholder={`{
  "Content-Type": "application/json",
  "Authorization": "Bearer @api_key"
}`}
                              name="headers"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Body (JSON)
                            </label>
                            <JsonMentionTextarea
                              value={JSON.stringify(apiConfig.body_template, null, 2)}
                              onChange={(e) => {
                                try {
                                  const body = JSON.parse(e.target.value);
                                  setApiConfig(prev => ({ ...prev, body_template: body }));
                                } catch (err) {
                                  // Invalid JSON, just update the text
                                }
                              }}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                              placeholder={`{
  "customer_name": "@customer_name",
  "customer_email": "@customer_email",
  "call_summary": "@call_summary",
  "call_duration": "@call_duration",
  "favorite_color": "@favorite_color",
  "preferred_service": "@preferred_service"
}`}
                              name="body_template"
                            />
                          </div>

                          {/* Advanced API Options */}
                          <div className="space-y-4 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700">Advanced Options</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Authentication Type
                                </label>
                                <select
                                  value={apiConfig.auth_type || 'none'}
                                  onChange={(e) => setApiConfig(prev => ({ ...prev, auth_type: e.target.value }))}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="none">None</option>
                                  <option value="bearer">Bearer Token</option>
                                  <option value="basic">Basic Auth</option>
                                  <option value="api_key">API Key</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Timeout (seconds)
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  max="300"
                                  value={apiConfig.timeout_seconds || 30}
                                  onChange={(e) => setApiConfig(prev => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 30 }))}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="30"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Retry Attempts
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={apiConfig.retry_attempts || 3}
                                  onChange={(e) => setApiConfig(prev => ({ ...prev, retry_attempts: parseInt(e.target.value) || 3 }))}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="3"
                                />
                              </div>

                              {apiConfig.auth_type && apiConfig.auth_type !== 'none' && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Auth Configuration (JSON)
                                  </label>
                                  <JsonMentionTextarea
                                    value={JSON.stringify(apiConfig.auth_config || {}, null, 2)}
                                    onChange={(e) => {
                                      try {
                                        const authConfig = JSON.parse(e.target.value);
                                        setApiConfig(prev => ({ ...prev, auth_config: authConfig }));
                                      } catch (err) {
                                        // Invalid JSON, just update the text
                                      }
                                    }}
                                    questions={getMentionVariables()}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                                    placeholder={`{
  "token": "@api_token",
  "username": "@username",
  "password": "@password"
}`}
                                    name="auth_config"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Phone Configuration */}
                      {formData.actionType === 'phone' && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              To Number
                            </label>
                            <MentionInput
                              value={phoneConfig.to_number_template}
                              onChange={(e) => setPhoneConfig(prev => ({ ...prev, to_number_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Type @ to insert variables like @customer_phone or use +1234567890"
                              name="to_number_template"
                            />
                          </div>

                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-700 flex items-center">
                              <Phone className="w-4 h-4 mr-2" />
                              Call will be made from your AI phone number
                            </p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              AI Script
                            </label>
                            <MentionInput
                              value={phoneConfig.script_template}
                              onChange={(e) => setPhoneConfig(prev => ({ ...prev, script_template: e.target.value }))}
                              questions={getMentionVariables()}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Type @ to insert variables like: Hi @customer_name, this is a follow-up call from @company_name. During our earlier conversation, you mentioned your favorite color is @favorite_color."
                              name="script_template"
                              multiline={true}
                              rows={8}
                            />
                          </div>

                          {/* Advanced Phone Options */}
                          <div className="space-y-4 pt-4 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700">Advanced Options</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Delay (minutes)
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max="1440"
                                  value={phoneConfig.delay_minutes || 0}
                                  onChange={(e) => setPhoneConfig(prev => ({ ...prev, delay_minutes: parseInt(e.target.value) || 0 }))}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="0"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  How long to wait after the call ends before making this call
                                </p>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Max Attempts
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={phoneConfig.max_attempts || 3}
                                  onChange={(e) => setPhoneConfig(prev => ({ ...prev, max_attempts: parseInt(e.target.value) || 3 }))}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="3"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Maximum number of call attempts if busy/no answer
                                </p>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Transfer Number
                                </label>
                                <MentionInput
                                  value={phoneConfig.transfer_number || ''}
                                  onChange={(e) => setPhoneConfig(prev => ({ ...prev, transfer_number: e.target.value }))}
                                  questions={getMentionVariables()}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Optional transfer number"
                                  name="transfer_number"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Number to transfer to if needed during the call
                                </p>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Voicemail Script
                                </label>
                                <MentionInput
                                  value={phoneConfig.voicemail_template || ''}
                                  onChange={(e) => setPhoneConfig(prev => ({ ...prev, voicemail_template: e.target.value }))}
                                  questions={getMentionVariables()}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                  placeholder="Type @ for variables: Hi @customer_name, this is @company_name following up on your call..."
                                  name="voicemail_template"
                                  multiline={true}
                                  rows={4}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Message to leave if call goes to voicemail
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>

                    {/* Delete Section - Only show when editing */}
                    {actionToEdit && (
                      <motion.div 
                        className="space-y-6"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6, duration: 0.3 }}
                      >
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="w-1 h-6 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
                          <h3 className="text-lg font-medium text-gray-900 tracking-wide">Delete Action</h3>
                        </div>

                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                          <div className="flex items-start">
                            <WarningCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-red-900 mb-2">Delete this post call action</h4>
                              <p className="text-sm text-red-700 mb-4">
                                This action cannot be undone. This will permanently delete the post call action and all its associated settings.
                              </p>
                              <motion.button
                                type="button"
                                disabled={loading}
                                onClick={handleDelete}
                                className={cn(
                                  "px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-xl transition-all duration-200",
                                  "focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-1",
                                  "disabled:opacity-60 disabled:cursor-not-allowed"
                                )}
                                whileHover={{ scale: loading ? 1 : 1.02 }}
                                whileTap={{ scale: loading ? 1 : 0.98 }}
                              >
                                {loading ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <CircleNotch className="animate-spin h-4 w-4 text-white" />
                                    <span>Deleting...</span>
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-2">
                                    <Trash className="h-4 w-4" />
                                    <span>Delete Action</span>
                                  </span>
                                )}
                              </motion.button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        // Original modal layout
        <>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <Timer className="w-6 h-6 text-gray-700 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">
                {actionToEdit ? 'Edit Post Call Action' : 'Create Post Call Action'}
              </h2>
            </div>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Rest of modal content - abbreviated for space */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="text-center py-12">
              <Timer className="w-16 h-16 text-purple-500 mb-4 mx-auto" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Modal Interface</h3>
              <p className="text-gray-600">
                Modal view is simplified. Use inline mode for full functionality.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <Button
              variant="outline"
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {actionToEdit ? 'Update' : 'Create'} Action
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );

  if (isInline) {
    return (
      <>
        {renderContent()}
        <CreateCustomFieldModal
          isOpen={isCustomFieldModalOpen}
          onClose={() => setIsCustomFieldModalOpen(false)}
          supabase={supabase}
          onSuccess={handleCreateCustomField}
        />
      </>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose(false)}
      >
        {renderContent()}
      </motion.div>

      {/* Create Custom Field Modal */}
      <CreateCustomFieldModal
        isOpen={isCustomFieldModalOpen}
        onClose={() => setIsCustomFieldModalOpen(false)}
        supabase={supabase}
        onSuccess={handleCreateCustomField}
      />
    </AnimatePresence>
  );
};

export default PostCallActionModal; 